"use client";

import { useEffect, useState } from "react";
import { useAnalytics } from "@/hooks/useAnalytics";

interface Props {
  label: string;
  sub?: string;
  icp: "rodziny" | "it" | "relokacja" | "zamiana" | "home";
  showAfterPx?: number;
  onClick?: () => void;
  closable?: boolean;
  icon?: string;
}

export default function FloatingCTA({
  label,
  sub,
  icp,
  showAfterPx = 800,
  onClick: onClickProp,
  icon = "📞",
  closable = false,
}: Props) {
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    let firedVisible = false;
    const onScroll = () => {
      const shouldShow = window.scrollY > showAfterPx;
      setVisible(shouldShow);
      if (shouldShow && !firedVisible) {
        firedVisible = true;
        trackEvent("floating_cta_visible", "icp_page", `floating-${icp}`, {
          icp,
          scroll_threshold: showAfterPx,
        });
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [trackEvent, icp, showAfterPx]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    trackEvent("floating_cta_clicked", "lead_capture", `floating-${icp}`, { icp });
    if (onClickProp) {
      e.preventDefault();
      onClickProp();
    }
  };

  // If onClick is provided — render as standalone button (no .open-callback delegation).
  // Otherwise — fall back to legacy .open-callback class for ClientBits delegation.
  const className = `floating-cta ${onClickProp ? "" : "open-callback"} ${visible ? "floating-cta--visible" : ""}`.trim();

  if (closed) return null;

  const btn = (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      data-cta-source={`floating-${icp}`}
      aria-label={label}
    >
      <span className="floating-cta-icon" aria-hidden="true">{icon}</span>
      <span className="floating-cta-text">
        <strong>{label}</strong>
        {sub && <span>{sub}</span>}
      </span>
    </button>
  );

  if (!closable) return btn;
  return (
    <div className="floating-cta-wrap">
      {btn}
      <button
        type="button"
        className={"floating-cta-close" + (visible ? " floating-cta-close--on" : "")}
        aria-label="Ukryj widżet PDF"
        onClick={() => setClosed(true)}
      >
        ×
      </button>
    </div>
  );
}
